import { useMemo, type RefObject } from 'react';
import { Gesture } from 'react-native-gesture-handler';
import {
  useDerivedValue,
  withSpring,
  type SharedValue,
} from 'react-native-reanimated';

import { use3DRotationStyle } from './hooks/use-3d-rotation-style';

export const AWARE_CARD_MAX_ROTATE_DEG = 18;

/** Finger offset from card center (as fraction of width/height) that maps to full tilt; beyond is clamped. */
export const AWARE_LIST_TILT_CLAMP_FRAC = 0.15;

const delayedSpringConfig = {
  stiffness: 100,
  damping: 10,
  mass: 1,
};

/**
 * List-row 3D split: `touchX` / `touchY` in card space (0…w, 0…h). Drive from `useJellyGesture` (tap) +
 * `useListRowTiltDragGesture` (press-and-drag). Faster vs delayed `withSpring` on the two columns.
 */
const FALLBACK_CARD_WIDTH = 360;

const resolve_width = (width: number | SharedValue<number>): number => {
  'worklet';
  if (typeof width === 'number') {
    return width > 0 ? width : FALLBACK_CARD_WIDTH;
  }
  const w = width.value;
  return w > 0 ? w : FALLBACK_CARD_WIDTH;
};

export const useDualLayerTiltFromTouch = (p: {
  /** Raw touch X in the card, same as gesture event `e.x` */
  touchX: SharedValue<number>;
  /** Raw touch Y in the card */
  touchY: SharedValue<number>;
  /**
   * Card width. Prefer a SharedValue updated in `onLayout` so `.value` is never
   * read during React render (Reanimated strict mode).
   */
  width: number | SharedValue<number>;
  height: number;
  /** When `false`, returns identity transforms (list rows — avoids 3D tilt cost while scrolling). */
  enabled?: boolean;
  /**
   * Maps full tilt to finger within ±(this fraction × width/height) of card center; longer drags do not tilt further.
   * @default AWARE_LIST_TILT_CLAMP_FRAC (0.15)
   */
  clampFromCenterFrac?: number;
}) => {
  const enabled = p.enabled !== false;
  const h = p.height > 0 ? p.height : 1;
  const frac = p.clampFromCenterFrac ?? AWARE_LIST_TILT_CLAMP_FRAC;
  const { touchX, touchY, width } = p;

  const dFastX = useDerivedValue(() => {
    const w = resolve_width(width);
    if (!enabled) {
      return w * 0.5;
    }
    const c = w * 0.5;
    const span = w * frac;
    const cx = Math.min(Math.max(touchX.value, c - span), c + span);
    return withSpring(cx);
  });
  const dFastY = useDerivedValue(() => {
    if (!enabled) {
      return h * 0.5;
    }
    const c = h * 0.5;
    const span = h * frac;
    const cy = Math.min(Math.max(touchY.value, c - span), c + span);
    return withSpring(cy);
  });
  const dSlowX = useDerivedValue(() => {
    const w = resolve_width(width);
    if (!enabled) {
      return w * 0.5;
    }
    const c = w * 0.5;
    const span = w * frac;
    const cx = Math.min(Math.max(touchX.value, c - span), c + span);
    return withSpring(cx, delayedSpringConfig);
  });
  const dSlowY = useDerivedValue(() => {
    if (!enabled) {
      return h * 0.5;
    }
    const c = h * 0.5;
    const span = h * frac;
    const cy = Math.min(Math.max(touchY.value, c - span), c + span);
    return withSpring(cy, delayedSpringConfig);
  });

  const { rRotationStyle: rTiltFaster } = use3DRotationStyle({
    x: dFastX,
    y: dFastY,
    maxSize: width,
    maxRotation: AWARE_CARD_MAX_ROTATE_DEG,
    interpolateXMax: width,
    interpolateYMax: h,
  });
  const { rRotationStyle: rTiltSlower } = use3DRotationStyle({
    x: dSlowX,
    y: dSlowY,
    maxSize: width,
    maxRotation: AWARE_CARD_MAX_ROTATE_DEG,
    perspective: 500,
    interpolateXMax: width,
    interpolateYMax: h,
  });

  return { rTiltFaster, rTiltSlower };
};

/**
 * `Pan` that updates `touchX` / `touchY` while the finger moves (press-and-drag aware tilt).
 * Activators match `useJellyGesture`’s jelly `Pan` so vertical list scroll still wins; combine
 * with `Gesture.Simultaneous(jellyRowGesture, thisGesture)`.
 */
export const useListRowTiltDragGesture = (o: {
  touchX: SharedValue<number>;
  touchY: SharedValue<number>;
  fullWidthRef: SharedValue<number>;
  height: number;
  scrollGestureRef?: RefObject<unknown>;
  /** When `false`, skips the extra pan recognizer (list scroll stays smooth). */
  enabled?: boolean;
}) => {
  const { touchX, touchY, fullWidthRef, height, scrollGestureRef, enabled = true } = o;

  return useMemo(() => {
    if (!enabled) {
      return Gesture.Manual();
    }
    const resetTilt = () => {
      'worklet';
      const wv = fullWidthRef.value;
      if (wv > 0) {
        touchX.value = wv * 0.5;
      }
      touchY.value = height * 0.5;
    };

    let p = Gesture.Pan()
      .minDistance(15)
      .activeOffsetY([-12, 12])
      .failOffsetY([-8, 8])
      .failOffsetX([-100, 100])
      .onBegin((e) => {
        'worklet';
        touchX.value = e.x;
        touchY.value = e.y;
      })
      .onChange((e) => {
        'worklet';
        touchX.value = e.x;
        touchY.value = e.y;
      })
      .onFinalize(() => {
        'worklet';
        resetTilt();
      });

    if (scrollGestureRef != null) {
      try {
        p = p.requireExternalGestureToFail(scrollGestureRef as never);
      } catch (_) {
        // Ref type mismatch on some RN versions
      }
    }
    return p;
  }, [enabled, fullWidthRef, height, scrollGestureRef, touchX, touchY]);
};
